import React from 'react';

const PrivacyPolicyEN: React.FC = () => {
  return (
    <div className="prose max-w-none">
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
        <p className="mb-4">
          BIXMO SRL ("we," "our," or "us") operates the GatherPlay mobile application and website (the "Service"). 
          This Privacy Policy explains how we collect, use, and protect information when you use our Service.
        </p>
        <div className="bg-gray-100 p-4 rounded-lg mb-4">
          <p><strong>Contact Information:</strong></p>
          <ul className="list-none mt-2 space-y-1">
            <li>Company: BIXMO SRL</li>
            <li>Address: Chisinau, Moldova</li>
            <li>Email: gatherplay.application@gmail.com</li>
          </ul>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Information We Collect</h2>
        
        <h3 className="text-xl font-semibold text-gray-700 mb-3">2.1 Information We Do NOT Collect</h3>
        <p className="mb-4">
          GatherPlay is designed with privacy in mind. We do not require user registration, and we do not collect personal information such as:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li>Names, email addresses, or phone numbers</li>
          <li>Demographic information</li>
          <li>Location data beyond basic country-level analytics</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-700 mb-3">2.2 Information We Do Collect</h3>
        <p className="mb-3"><strong>Automatically Collected Information:</strong></p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li><strong>Device Information:</strong> Device type, operating system version, app version</li>
          <li><strong>Usage Analytics:</strong> How you interact with the app, features used, session duration (collected via Amplitude)</li>
          <li><strong>Technical Data:</strong> Crash reports and performance metrics to improve app stability</li>
        </ul>

        <p className="mb-3"><strong>Local Device Storage:</strong></p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li><strong>Player ID:</strong> A randomly generated identifier stored locally on your device to ensure game scenarios don't repeat for the same player</li>
          <li><strong>Game Preferences:</strong> Settings and preferences you configure within the app</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Use Information</h2>
        <p className="mb-4">We use the collected information to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Improve app performance and fix bugs</li>
          <li>Understand how users interact with GatherPlay to enhance the gaming experience</li>
          <li>Ensure game scenarios don't repeat for individual players</li>
          <li>Display relevant advertisements (in the free version)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Information Sharing and Disclosure</h2>
        <p className="mb-3"><strong>Third-Party Services:</strong></p>
        <ul className="list-disc pl-6 mb-4 space-y-1">
          <li><strong>Amplitude:</strong> We use Amplitude for analytics to understand app usage and improve user experience</li>
          <li><strong>Google Ads:</strong> We display advertisements through Google's advertising network in the free version of the app</li>
          <li><strong>App Stores:</strong> Basic usage statistics may be shared with Apple App Store and Google Play Store as part of their standard analytics</li>
        </ul>
        <p>We do not sell, trade, or otherwise transfer your information to third parties beyond these service providers.</p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Advertising</h2>
        <p>
          The free version of GatherPlay displays advertisements provided by Google Ads. These ads may be based on general 
          demographics and interests but do not use personal information collected from our app. You can upgrade to the premium 
          version to remove all advertisements through an in-app purchase.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect information against unauthorized access, 
          alteration, disclosure, or destruction. Since we collect minimal data, your privacy risk is significantly reduced.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Children's Privacy</h2>
        <p>
          GatherPlay is suitable for users aged 6 and above. We do not knowingly collect personal information from children. 
          If you are a parent or guardian and believe your child has provided us with personal information, please contact us 
          at gatherplay.application@gmail.com.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Data Retention</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Analytics Data:</strong> Retained for up to 2 years to analyze trends and improve the service</li>
          <li><strong>Local Device Data:</strong> Stored locally on your device and can be cleared by uninstalling the app</li>
          <li><strong>Technical Data:</strong> Crash reports and performance data retained for up to 1 year</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Your Rights</h2>
        <p className="mb-4">You have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Request information about what data we have collected (limited as described above)</li>
          <li>Request deletion of your data by contacting us</li>
          <li>Opt out of analytics by disabling them in your device settings</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. International Data Transfers</h2>
        <p>
          Our analytics provider (Amplitude) may process data in countries other than Moldova. We ensure appropriate 
          safeguards are in place for any international data transfers.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
          Privacy Policy within the app and updating the "Last Updated" date. Continued use of the Service after 
          changes constitutes acceptance of the updated policy.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Contact Us</h2>
        <p className="mb-4">If you have any questions about this Privacy Policy, please contact us:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Email: gatherplay.application@gmail.com</li>
          <li>Company: BIXMO SRL, Chisinau, Moldova</li>
        </ul>
      </section>

      <div className="bg-blue-50 p-4 rounded-lg mt-8">
        <p className="text-sm text-blue-800">
          This Privacy Policy is designed to be transparent about our minimal data collection practices while ensuring 
          compliance with applicable privacy laws including GDPR, CCPA, and mobile app store requirements.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyEN;